// B2BES [AST-359] - ADD Customization - Part of AST-359 EVO Payments
import { adjustIFrameHeight, adjustIFrameWidth } from "@auer-steel/Handlers/Utilities";
import { WalletPageConnection } from "@auer-steel/Services/AuerSteelApiModels";
import { createAchAccount, getWalletPageConnection } from "@auer-steel/Services/WalletService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateCreditCard from "@insite/client-framework/Common/Utilities/validateCreditCard";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface CreateWalletButtonStyles {
    container?: GridContainerProps;
    buttonStyles?: InjectableCss;
    fullWidthGridItem?: GridItemProps;
    spinnerWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    createButton?: InjectableCss;
    acceptTNC?: InjectableCss;
}

export const styles: CreateWalletButtonStyles = {
    container: {
        gap: 0,
    },
    buttonStyles: {
        css: css`
            margin: 5px 0px;
        `,
    },
    fullWidthGridItem: {
        width: 11,
    },
    spinnerWrapper: {
        css: css`
            height: 50px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    createButton: {
        css: css`
        display : flex;
        justify-content : flex-end;
        padding-top : 0px;
        `,
    },
    acceptTNC :{
        css: css`
        padding-top : 0%;
        padding-right : 0%;
        display : flex;
        flex-direction: row;
        div[data-id="checkbox"] {
            padding-left : 10px;
        }
        `,
    }
}

type Props = {
    modalCloseHandler: any,
}

export const CreateWallet: React.FC<Props> = (props) => {
    const [walletPageConnection, setWalletPageConnection] = React.useState<WalletPageConnection>();
    const [isShowCreateIFrame, setIsShowCreateIFrame] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);
    const toasterContext = React.useContext(ToasterContext);
    const [accountNumber, setAccountNumber] = React.useState("");
    const [routingNumber, setRoutingNumber] = React.useState("");
    const [accountType, setAccountType] = React.useState("");
    const [bankName, setBankName] = React.useState("");
    const [accountNumberError, setAccountNumberError] = React.useState("");
    const [routingNumberError, setRoutingNumberError] = React.useState("");
    const [bankNameError, setBankNameError] = React.useState("");
    const [errorFromApi, setErrorFromApi] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [createEnabled, setCreateEnabled] = React.useState(false);

    const hideSpinner = () => setIsLoading(false);
    const showSpinner = () => setIsLoading(true);

    const validateData = () => {
        setErrorFromApi("");
        const patternRouting = new RegExp("^\\d{9}$");
        const patternAccount = new RegExp("^\\d{4,20}$");
        const accountValid = patternAccount.test(accountNumber);
        const routingValid = patternRouting.test(routingNumber);
        let errors = false;
        if (accountValid) {
            setAccountNumberError("")
        }
        else {
            setAccountNumberError(siteMessage("ACHAccount_AccountNumberError") as string);
            errors = true;
        }
        if (routingValid) {
            setRoutingNumberError("")
        }
        else {
            setRoutingNumberError(siteMessage("ACHAccount_RoutingNumberError") as string);
            errors = true;
        }
        return errors;
    }

    const createAchAccountHandler = (event: any) => {   
        showSpinner();
        setIsSubmitting(true);
        const errors = validateData();  
        if (errors) {
            hideSpinner();
            setIsSubmitting(false);
            return;
        }   
        const paymentInformation = {
            AccountNumber: accountNumber,
            RoutingNumber: routingNumber,
            BankAccountType: "CHECKING",
            BankName: bankName
        };
        createAchAccount(paymentInformation).then((response: any) => {
            const result = response.result;
            if (result.success === false || result.success === "false") {
                setErrorFromApi(result.responseMessage);
            }
            else {
                toasterContext.addToast({ body: siteMessage("ACHAccount_Success"), messageType: "success" });
                props.modalCloseHandler();
                hideSpinner();
            }
            setIsSubmitting(false);
        });
    };

    const handleAccountNumberChange = (event: any) => {        
        const { target } = event;
        setAccountNumber(event.target.value);
        setAccountNumberError("");
    };

    const handleRoutingNumberChange = (event: any) => {        
        const { target } = event;
        setRoutingNumber(event.target.value);
        setRoutingNumberError("");
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.fullWidthGridItem}>

                <StyledWrapper>
                    <GridContainer>
                        <GridItem width={4}>
                            <TextField
                                label={translate("Account Number")}
                                required
                                name="AccountNumber"
                                onChange={handleAccountNumberChange}
                                placeholder={accountNumber}
                                maxLength={30}
                                error={accountNumberError}
                            />
                        </GridItem>
                        <GridItem width={4}>
                            <TextField
                                label={translate("Routing Number")}
                                required
                                name="RoutingNumber"
                                onChange={handleRoutingNumberChange}
                                placeholder={routingNumber}
                                maxLength={9}
                                error={routingNumberError}
                            />
                        </GridItem>
                        <GridItem width={4}>
                            <TextField
                                label={translate("Account Type")}
                                value="CHECKING"
                                disabled
                                name="AccountType"
                            />
                        </GridItem>
                        {errorFromApi !== "" && 
                            <GridItem width={12}>
                                <Typography variant="p" style={{color: "red", fontWeight: "bold"}}>
                                    {errorFromApi}
                                </Typography>
                            </GridItem>
                        }
                        

                        <GridItem width={12}>
                            {siteMessage("T&C_For_Create_Account_On_ACH")}
                        </GridItem>
                        <GridItem width={6} {...styles.acceptTNC}>
                            <CheckboxGroup {...styles.acceptTNC}>
                            {siteMessage("Accept_T&C_For_Create_Account_On_ACH")}
                            <Checkbox
                                    onChange={(e, value) => setCreateEnabled(value)}
                                    checked={createEnabled}
                                ></Checkbox>
                            </CheckboxGroup>
                        </GridItem>
                        
                        <GridItem width={6} {...styles.createButton} >
                            {isSubmitting ? (
                                <StyledWrapper {...styles.spinnerWrapper}>
                                    <LoadingSpinner {...styles.spinner}></LoadingSpinner>
                                </StyledWrapper>
                            ) : (
                                <Button onClick={createAchAccountHandler} disabled={!createEnabled}>
                                    Create
                                </Button>
                            )}
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </GridItem>
        </GridContainer >);
}
